import React, { useState } from 'react';
import { useUserData } from '../../utils/UserDataContext';

interface FormField {
  name: string;
  label: string;
  type: string;
  options?: { value: string; label: string }[];
  placeholder?: string;
  required?: boolean;
  defaultValue?: any;
  width?: 'full' | 'half';
}

interface FinanceFormProps {
  title: string;
  description?: string;
  fields: FormField[];
  submitLabel: string;
  onSubmit: (data: any) => void;
  className?: string;
}

const FinanceForm: React.FC<FinanceFormProps> = ({
  title,
  description,
  fields,
  submitLabel,
  onSubmit,
  className = '',
}) => {
  const [formData, setFormData] = useState<Record<string, any>>(
    fields.reduce((acc, field) => {
      acc[field.name] = field.defaultValue || '';
      return acc;
    }, {} as Record<string, any>)
  );

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    let parsedValue: string | number | boolean = value;

    // Convert to number if it's a number input
    if (type === 'number') {
      parsedValue = value === '' ? 0 : Number(value);
    }

    // Convert to date string if it's a date input
    if (type === 'date' && value) {
      parsedValue = new Date(value).toISOString().split('T')[0];
    }

    setFormData((prev) => ({
      ...prev,
      [name]: parsedValue,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
    
    // Reset form if needed
    // setFormData(fields.reduce((acc, field) => {
    //   acc[field.name] = field.defaultValue || '';
    //   return acc;
    // }, {} as Record<string, any>));
  };

  return (
    <div className={`neo-card p-6 ${className}`}>
      <h2 className="text-xl font-semibold mb-2">{title}</h2>
      {description && <p className="text-text/70 mb-4">{description}</p>}
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {fields.map((field) => (
            <div 
              key={field.name} 
              className={`${field.width === 'full' ? 'md:col-span-2' : ''}`}
            >
              <label htmlFor={field.name} className="block text-sm font-medium mb-1">
                {field.label} {field.required && <span className="text-red-500">*</span>}
              </label>
              
              {field.type === 'select' ? (
                <select
                  id={field.name}
                  name={field.name}
                  value={String(formData[field.name])}
                  onChange={handleChange}
                  required={field.required}
                  className="input-field"
                >
                  <option value="">Select {field.label}</option>
                  {field.options?.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : field.type === 'textarea' ? (
                <textarea
                  id={field.name}
                  name={field.name}
                  value={String(formData[field.name])}
                  onChange={handleChange}
                  placeholder={field.placeholder}
                  required={field.required}
                  className="input-field"
                  rows={4}
                />
              ) : field.type === 'checkbox' ? (
                <div className="flex items-center">
                  <input
                    id={field.name}
                    name={field.name}
                    type="checkbox"
                    checked={Boolean(formData[field.name])}
                    onChange={(e) => {
                      setFormData((prev) => ({
                        ...prev,
                        [field.name]: e.target.checked,
                      }));
                    }}
                    className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
                  />
                  <label htmlFor={field.name} className="ml-2 text-sm">
                    {field.label}
                  </label>
                </div>
              ) : (
                <input
                  id={field.name}
                  name={field.name}
                  type={field.type}
                  value={field.type === 'number' ? Number(formData[field.name] || 0) : String(formData[field.name] || '')}
                  onChange={handleChange}
                  placeholder={field.placeholder}
                  required={field.required}
                  className="input-field"
                  step={field.type === 'number' ? '0.01' : undefined}
                />
              )}
            </div>
          ))}
        </div>
        
        <div className="flex justify-end mt-6">
          <button
            type="submit"
            className="neo-button"
          >
            {submitLabel}
          </button>
        </div>
      </form>
    </div>
  );
};

export default FinanceForm; 